// The courses on one event, each an uploaded GPX file with its start and
// course-close times — one route for a single race, one per distance for a race
// of several (100M, 100K, 50K), one per day for a stage race. All the same thing
// here; only the times differ.
//
// On the Event dialog beside the race tracking links, and saved as you go like
// them. The routes are drawn on the incidents map, and the weather safety beta
// analyses them. Figures (distance, ascent, high point) are worked out by the
// server from the file on upload.
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { API_BASE_URL } from "../config";
import { notify } from "../notify";
import { downloadBlob, fileSlug } from "../download";
import { useFullScreenDialog } from "../useFullScreenDialog";
import { fmtRouteWindow, routeFigures, routeStyle } from "../eventRoutes";

const ROUTES_API = `${API_BASE_URL}/event-routes`;

// <input type="datetime-local"> is in the browser's zone; convert to/from ISO.
const toLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const fromLocal = (s) => (s ? new Date(s).toISOString() : null);

const BLANK = { name: "", start: "", close: "", file: null };

/**
 * Props: eventId (a number once the dialog has an event), canEdit (Events.edit),
 * onSummary(text) — a one-line description for a collapsed section.
 */
export default function EventRouteManager({ eventId, canEdit, onSummary }) {
  const fullScreen = useFullScreenDialog();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null | "new" | route
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const fileRef = useRef(null);

  // Same guard as EventRaceManager: the dialog's id drops to null while it fades.
  const identified = typeof eventId === "number";

  const load = useCallback(() => {
    if (!identified) { setRoutes([]); setLoading(true); return; }
    axios.get(`${ROUTES_API}/`, { params: { event_id: eventId } })
      .then(res => setRoutes(res.data || []))
      .catch(() => setRoutes([]))
      .finally(() => setLoading(false));
  }, [identified, eventId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (loading) return;
    onSummary?.(routes.length
      ? `${routes.length} route${routes.length === 1 ? "" : "s"}: ${routes.map(r => r.name).join(", ")}`
      : "No routes uploaded");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes, loading]);

  const openNew = () => {
    // A new route defaults to the times of the last one — the next distance on
    // the same day starts from the same place in the form.
    const prev = routes[routes.length - 1];
    setForm({ ...BLANK, start: toLocal(prev?.start_at), close: toLocal(prev?.close_at) });
    setEditing("new");
  };
  const openEdit = (r) => {
    if (!canEdit) return;
    setForm({ name: r.name, start: toLocal(r.start_at), close: toLocal(r.close_at), file: null });
    setEditing(r);
  };
  const close = () => { setEditing(null); setForm(BLANK); };

  const timesOk = !(form.start && form.close && form.close <= form.start);
  const canSave = !busy && timesOk && (editing === "new" ? !!form.file : !!form.name.trim());

  const save = async () => {
    setBusy(true);
    try {
      if (editing === "new") {
        const body = new FormData();
        body.append("event_id", String(eventId));
        if (form.name.trim()) body.append("name", form.name.trim());
        if (form.start) body.append("start_at", fromLocal(form.start));
        if (form.close) body.append("close_at", fromLocal(form.close));
        body.append("file", form.file);
        await axios.post(`${ROUTES_API}/`, body);
        notify("Route uploaded.", "success");
      } else {
        await axios.put(`${ROUTES_API}/${editing.id}`, {
          name: form.name.trim(), start_at: fromLocal(form.start), close_at: fromLocal(form.close),
        });
        if (form.file) {
          const body = new FormData();
          body.append("file", form.file);
          await axios.put(`${ROUTES_API}/${editing.id}/gpx`, body);
        }
        notify("Route saved.", "success");
      }
      load();
      close();
    } catch {
      // the global interceptor surfaces the error
    } finally {
      setBusy(false);
    }
  };

  const download = (r) => {
    axios.get(`${ROUTES_API}/${r.id}/gpx`, { responseType: "blob" })
      .then(res => downloadBlob(res.data, r.original_filename || `${fileSlug(r.name, "route")}.gpx`))
      .catch(() => {});
  };

  const doDelete = () => {
    setBusy(true);
    axios.delete(`${ROUTES_API}/${confirmDelete.id}`)
      .then(() => { notify("Route removed.", "success"); setConfirmDelete(null); load(); })
      .catch(() => {})
      .finally(() => setBusy(false));
  };

  return (
    <Box>
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                <TableCell>Route</TableCell>
                <TableCell>Start – course close</TableCell>
                <TableCell align="right" padding="checkbox" />
              </TableRow>
            </TableHead>
            <TableBody>
              {routes.map((r, i) => {
                const st = routeStyle(i);
                return (
                  <TableRow hover key={r.id} onClick={() => openEdit(r)}
                            sx={{ cursor: canEdit ? "pointer" : "default" }}>
                    <TableCell>
                      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                        {/* The same line the map draws, as the key to it. */}
                        <svg width="28" height="8" aria-hidden="true" style={{ flexShrink: 0 }}>
                          <line x1="1" y1="4" x2="27" y2="4" stroke={st.color} strokeWidth="4"
                                strokeDasharray={st.dashArray || undefined} strokeLinecap="round" />
                        </svg>
                        <Box>
                          <Typography variant="body2">{r.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{routeFigures(r)}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={r.start_at && r.close_at ? "text.primary" : "text.secondary"}>
                        {fmtRouteWindow(r)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" padding="checkbox" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="Download GPX">
                        <IconButton size="small" aria-label={`Download ${r.name}`}
                                    onClick={(e) => { e.stopPropagation(); download(r); }}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canEdit && (
                        <Tooltip title="Remove">
                          <IconButton size="small" color="error" aria-label={`Remove ${r.name}`}
                                      onClick={(e) => { e.stopPropagation(); setConfirmDelete(r); }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {routes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ color: "text.secondary", py: 2 }}>
                    {loading ? <CircularProgress size={24} /> : "No routes uploaded."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {canEdit && (
        <Button size="small" startIcon={<AddIcon />} onClick={openNew} sx={{ mt: 1 }}>
          Upload route
        </Button>
      )}

      <Dialog open={editing !== null} onClose={close} fullWidth maxWidth="sm" fullScreen={fullScreen}>
        <DialogTitle>{editing === "new" ? "Upload route" : "Edit route"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <input ref={fileRef} type="file" accept=".gpx,application/gpx+xml" hidden
                     onChange={(e) => {
                       const file = e.target.files?.[0] || null;
                       e.target.value = "";
                       setForm(f => ({ ...f, file }));
                     }} />
              <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => fileRef.current?.click()}>
                {form.file ? "Choose a different file" : editing === "new" ? "Choose GPX file" : "Replace GPX file"}
              </Button>
              <Typography variant="body2" sx={{ mt: 0.5 }} color={form.file ? "text.primary" : "text.secondary"}>
                {form.file ? form.file.name
                  : editing === "new" ? "No file chosen." : `Current file: ${editing?.original_filename || "—"}`}
              </Typography>
            </Box>
            <TextField label="Name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                       required={editing !== "new"} placeholder='e.g. "100K", "Day 3"'
                       helperText={editing === "new" ? "Leave blank to use the name in the file." : undefined} />
            <Typography variant="body2" color="text.secondary">
              When the first runners set off and when the course closes. For several distances, or
              several days, give each route its own times.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField label="Start" type="datetime-local" value={form.start}
                         onChange={(e) => setForm(f => ({ ...f, start: e.target.value }))}
                         slotProps={{ inputLabel: { shrink: true } }} />
              <TextField label="Course close" type="datetime-local" value={form.close}
                         onChange={(e) => setForm(f => ({ ...f, close: e.target.value }))}
                         slotProps={{ inputLabel: { shrink: true } }}
                         error={!timesOk} helperText={!timesOk ? "Must be after the start." : undefined} />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>Cancel</Button>
          <Button variant="contained" disabled={!canSave} onClick={save}
                  startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
            {editing === "new" ? "Upload" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDelete !== null} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Remove route?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{confirmDelete?.name}" and its GPX file will be deleted, and it will no longer be drawn
            on the incidents map.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={busy}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteIcon />}
                  onClick={doDelete} disabled={busy}>Remove</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
